import React, { useState, useEffect, useCallback } from 'react';
import { useAuth, useLanguage } from '../context/AppContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Video, Monitor, Users, Clock, BookOpen, FileText, Link as LinkIcon,
  ExternalLink, Download, Plus, Trash2, ChevronRight, GraduationCap,
  Calendar, Play, Globe, BookMarked, File, Layers, Settings
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RESOURCE_TYPES = {
  syllabus: { label: 'Syllabus', icon: BookMarked, color: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
  book: { label: 'Livre / Manuel', icon: BookOpen, color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  pdf: { label: 'Document PDF', icon: FileText, color: 'bg-red-50 text-red-700 border-red-200' },
  video: { label: 'Video', icon: Play, color: 'bg-purple-50 text-purple-700 border-purple-200' },
  link: { label: 'Lien Web', icon: Globe, color: 'bg-blue-50 text-blue-700 border-blue-200' },
  other: { label: 'Autre', icon: File, color: 'bg-gray-50 text-gray-700 border-gray-200' },
};

export const OnlineCoursesPage = () => {
  const { user } = useAuth();
  const { language } = useLanguage();
  const [classrooms, setClassrooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedClassroom, setSelectedClassroom] = useState(null);
  const [activeTab, setActiveTab] = useState('classroom');

  const isStudent = user?.role === 'student';
  const isTeacher = ['teacher', 'visiting_teacher'].includes(user?.role);
  const isAdmin = !isStudent && !isTeacher;

  const fetchClassrooms = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/online-courses/my-classrooms`);
      const data = Array.isArray(res.data) ? res.data : [];
      setClassrooms(data);
      if (data.length === 1) setSelectedClassroom(data[0]);
    } catch (err) {
      console.error('Error fetching classrooms:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchClassrooms(); }, [fetchClassrooms]);

  const l = language === 'fr' ? {
    title: 'Cours en Ligne',
    subtitle: 'Salles virtuelles et ressources pedagogiques',
    classroom: 'Salle Virtuelle',
    schedule: 'Emploi du Temps',
    resources: 'Ressources',
    joinRoom: 'Rejoindre la Salle',
    roomNotConfigured: 'Salle non configuree',
    roomNotConfiguredDesc: 'Le lien de la salle virtuelle sera configure prochainement par l\'administration.',
    noClassrooms: 'Aucune salle virtuelle disponible',
    noClassroomsDesc: 'Vous n\'etes inscrit dans aucune promotion active.',
    students: 'etudiants',
    courses: 'cours',
    todaySchedule: 'Emploi du temps du jour',
    noScheduleToday: 'Aucun cours prevu aujourd\'hui',
    allCourses: 'Tous les cours de la promotion',
    addResource: 'Ajouter une Ressource',
    noResources: 'Aucune ressource disponible',
    noResourcesDesc: 'Les enseignants n\'ont pas encore ajoute de ressources.',
    openLink: 'Ouvrir',
    downloadLink: 'Telecharger',
    addedBy: 'Ajoute par',
    selectPromotion: 'Selectionnez une promotion',
    resourceTitle: 'Titre de la ressource',
    resourceUrl: 'Lien URL',
    resourceType: 'Type de ressource',
    resourceDesc: 'Description (optionnel)',
    selectCourse: 'Cours concerne',
    save: 'Enregistrer',
    cancel: 'Annuler',
    deleteConfirm: 'Supprimer cette ressource ?',
    comingSoon: 'Bientot disponible',
  } : {
    title: 'Online Courses',
    subtitle: 'Virtual classrooms and teaching resources',
    classroom: 'Virtual Classroom',
    schedule: 'Schedule',
    resources: 'Resources',
    joinRoom: 'Join Room',
    roomNotConfigured: 'Room not configured',
    roomNotConfiguredDesc: 'The virtual room link will be configured soon by the administration.',
    noClassrooms: 'No virtual classroom available',
    noClassroomsDesc: 'You are not enrolled in any active promotion.',
    students: 'students',
    courses: 'courses',
    todaySchedule: 'Today\'s schedule',
    noScheduleToday: 'No courses scheduled today',
    allCourses: 'All promotion courses',
    addResource: 'Add Resource',
    noResources: 'No resources available',
    noResourcesDesc: 'Teachers haven\'t added any resources yet.',
    openLink: 'Open',
    downloadLink: 'Download',
    addedBy: 'Added by',
    selectPromotion: 'Select a promotion',
    resourceTitle: 'Resource title',
    resourceUrl: 'URL link',
    resourceType: 'Resource type',
    resourceDesc: 'Description (optional)',
    selectCourse: 'Related course',
    save: 'Save',
    cancel: 'Cancel',
    deleteConfirm: 'Delete this resource?',
    comingSoon: 'Coming soon',
  };

  if (loading) {
    return (
      <div className="space-y-6 animate-slide-in" data-testid="online-courses-page">
        <div>
          <h1 className="heading-2">{l.title}</h1>
          <p className="text-gray-500 mt-1">{l.subtitle}</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[1, 2].map(i => (
            <Card key={i} className="animate-pulse">
              <CardContent className="p-6">
                <div className="h-5 w-32 bg-gray-200 rounded mb-3" />
                <div className="h-8 w-48 bg-gray-200 rounded mb-4" />
                <div className="h-4 w-24 bg-gray-200 rounded" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (classrooms.length === 0) {
    return (
      <div className="space-y-6 animate-slide-in" data-testid="online-courses-page">
        <div>
          <h1 className="heading-2">{l.title}</h1>
          <p className="text-gray-500 mt-1">{l.subtitle}</p>
        </div>
        <Card>
          <CardContent className="p-12 text-center">
            <Monitor className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">{l.noClassrooms}</h3>
            <p className="text-gray-500">{l.noClassroomsDesc}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-slide-in" data-testid="online-courses-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="heading-2">{l.title}</h1>
          <p className="text-gray-500 mt-1">{l.subtitle}</p>
        </div>
      </div>

      {/* Promotion Selector (if multiple) */}
      {classrooms.length > 1 && !selectedClassroom && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {classrooms.map(cr => (
            <Card
              key={cr.promotion_id}
              className="card-hover cursor-pointer group"
              onClick={() => setSelectedClassroom(cr)}
              data-testid={`classroom-card-${cr.promotion_id}`}
            >
              <CardContent className="p-6">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center mb-4">
                  <Monitor className="w-6 h-6 text-white" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-1 group-hover:text-[var(--tenant-primary)] transition-colors">
                  {cr.promotion_label}
                </h3>
                <p className="text-sm text-gray-500 mb-3">{cr.academic_year}</p>
                <div className="flex items-center gap-4 text-xs text-gray-400">
                  <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5" /> {cr.student_count} {l.students}</span>
                  <span className="flex items-center gap-1"><BookOpen className="w-3.5 h-3.5" /> {cr.courses?.length || 0} {l.courses}</span>
                </div>
                {cr.visio_link ? (
                  <Badge className="mt-3 bg-green-50 text-green-700 border-green-200">Salle active</Badge>
                ) : (
                  <Badge variant="outline" className="mt-3 text-gray-400">{l.comingSoon}</Badge>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Single classroom or selected */}
      {(selectedClassroom || classrooms.length === 1) && (
        <ClassroomView
          classroom={selectedClassroom || classrooms[0]}
          l={l}
          language={language}
          user={user}
          isTeacher={isTeacher}
          isStudent={isStudent}
          isAdmin={isAdmin}
          onBack={classrooms.length > 1 ? () => setSelectedClassroom(null) : null}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          onRefresh={fetchClassrooms}
        />
      )}
    </div>
  );
};

const ClassroomView = ({ classroom, l, language, user, isTeacher, isStudent, isAdmin, onBack, activeTab, setActiveTab, onRefresh }) => {
  const [resources, setResources] = useState([]);
  const [loadingResources, setLoadingResources] = useState(false);
  const [showAddResource, setShowAddResource] = useState(false);

  const fetchResources = useCallback(async () => {
    setLoadingResources(true);
    try {
      const res = await axios.get(`${API_URL}/api/promotions/${classroom.promotion_id}/all-resources`);
      setResources(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingResources(false);
    }
  }, [classroom.promotion_id]);

  useEffect(() => {
    if (activeTab === 'resources') fetchResources();
  }, [activeTab, fetchResources]);

  const handleDeleteResource = async (resourceId) => {
    if (!window.confirm(l.deleteConfirm)) return;
    try {
      await axios.delete(`${API_URL}/api/resources/${resourceId}`);
      toast.success('Ressource supprimee');
      fetchResources();
    } catch {
      toast.error('Erreur lors de la suppression');
    }
  };

  return (
    <div className="space-y-4">
      {onBack && (
        <Button variant="ghost" onClick={onBack} className="gap-2 text-gray-500 -ml-2" data-testid="back-to-classrooms">
          <ChevronRight className="w-4 h-4 rotate-180" />
          {language === 'fr' ? 'Toutes les promotions' : 'All promotions'}
        </Button>
      )}

      {/* Classroom Header */}
      <Card className="border-0 bg-gradient-to-r from-[#0F2C59] to-[#1a3f7a] text-white overflow-hidden">
        <CardContent className="p-6 sm:p-8 relative">
          <div className="absolute right-0 top-0 w-40 h-40 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/4" />
          <div className="absolute right-16 bottom-0 w-24 h-24 bg-white/5 rounded-full translate-y-1/2" />
          <div className="relative z-10">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-lg bg-white/15 flex items-center justify-center">
                <GraduationCap className="w-5 h-5" />
              </div>
              <div>
                <h2 className="text-xl sm:text-2xl font-bold">{classroom.promotion_label}</h2>
                <p className="text-blue-200 text-sm">{classroom.academic_year}</p>
              </div>
            </div>
            <div className="flex items-center gap-6 mt-4 text-sm text-blue-200">
              <span className="flex items-center gap-1.5"><Users className="w-4 h-4" /> {classroom.student_count} {l.students}</span>
              <span className="flex items-center gap-1.5"><BookOpen className="w-4 h-4" /> {classroom.courses?.length || 0} {l.courses}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="classroom" data-testid="tab-classroom">
            <Monitor className="w-4 h-4 mr-2 hidden sm:block" />
            {l.classroom}
          </TabsTrigger>
          <TabsTrigger value="schedule" data-testid="tab-schedule">
            <Calendar className="w-4 h-4 mr-2 hidden sm:block" />
            {l.schedule}
          </TabsTrigger>
          <TabsTrigger value="resources" data-testid="tab-resources">
            <BookMarked className="w-4 h-4 mr-2 hidden sm:block" />
            {l.resources}
          </TabsTrigger>
        </TabsList>

        {/* TAB 1: Virtual Classroom */}
        <TabsContent value="classroom" className="mt-4 space-y-4">
          <VirtualClassroomTab classroom={classroom} l={l} language={language} isAdmin={isAdmin} onLinkUpdated={onRefresh} />
        </TabsContent>

        {/* TAB 2: Today's Schedule */}
        <TabsContent value="schedule" className="mt-4 space-y-4">
          <ScheduleTab classroom={classroom} l={l} language={language} />
        </TabsContent>

        {/* TAB 3: Resources */}
        <TabsContent value="resources" className="mt-4 space-y-4">
          {isTeacher && (
            <div className="flex justify-end">
              <Button onClick={() => setShowAddResource(true)} className="gap-2" data-testid="add-resource-button">
                <Plus className="w-4 h-4" />
                {l.addResource}
              </Button>
            </div>
          )}
          <ResourcesTab
            resources={resources}
            loading={loadingResources}
            l={l}
            language={language}
            isTeacher={isTeacher}
            userId={user?.id}
            onDelete={handleDeleteResource}
          />
          {showAddResource && (
            <AddResourceDialog
              courses={classroom.courses || []}
              l={l}
              language={language}
              onClose={() => setShowAddResource(false)}
              onSuccess={() => { setShowAddResource(false); fetchResources(); }}
            />
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

// ============ VIRTUAL CLASSROOM TAB ============
const VirtualClassroomTab = ({ classroom, l, language, isAdmin, onLinkUpdated }) => {
  const hasLink = !!classroom.visio_link;
  const [editingLink, setEditingLink] = useState(false);
  const [meetLink, setMeetLink] = useState(classroom.visio_link || '');
  const [saving, setSaving] = useState(false);

  const saveLink = async () => {
    if (!meetLink.trim()) return;
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/promotions/${classroom.promotion_id}/visio-link`, {
        visio_link: meetLink.trim()
      });
      toast.success(language === 'fr' ? 'Lien Google Meet configure' : 'Google Meet link configured');
      setEditingLink(false);
      if (onLinkUpdated) onLinkUpdated();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  const removeLink = async () => {
    if (!window.confirm(language === 'fr' ? 'Supprimer le lien Google Meet ?' : 'Remove Google Meet link?')) return;
    setSaving(true);
    try {
      await axios.delete(`${API_URL}/api/promotions/${classroom.promotion_id}/visio-link`);
      toast.success(language === 'fr' ? 'Lien supprime' : 'Link removed');
      setMeetLink('');
      if (onLinkUpdated) onLinkUpdated();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Admin: Configure Meet Link */}
      {isAdmin && (
        <Card className="border-amber-200 bg-amber-50/30" data-testid="admin-meet-config">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-3">
              <Settings className="w-4 h-4 text-amber-600" />
              <h4 className="font-semibold text-sm text-amber-800">
                {language === 'fr' ? 'Configuration Admin - Lien Google Meet' : 'Admin Config - Google Meet Link'}
              </h4>
            </div>
            {editingLink ? (
              <div className="space-y-3">
                <Input
                  value={meetLink}
                  onChange={(e) => setMeetLink(e.target.value)}
                  placeholder="https://meet.google.com/xxx-yyyy-zzz"
                  className="bg-white"
                  data-testid="meet-link-input"
                />
                <p className="text-xs text-amber-600">
                  {language === 'fr'
                    ? 'Ce lien sera partage avec tous les etudiants de cette promotion.'
                    : 'This link will be shared with all students in this promotion.'}
                </p>
                <div className="flex gap-2">
                  <Button size="sm" onClick={saveLink} disabled={saving || !meetLink.trim()} data-testid="save-meet-link">
                    {saving ? (language === 'fr' ? 'Enregistrement...' : 'Saving...') : (language === 'fr' ? 'Enregistrer' : 'Save')}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => { setEditingLink(false); setMeetLink(classroom.visio_link || ''); }}>
                    {language === 'fr' ? 'Annuler' : 'Cancel'}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-between">
                <div>
                  {hasLink ? (
                    <div className="flex items-center gap-2">
                      <LinkIcon className="w-4 h-4 text-green-600" />
                      <span className="text-sm text-gray-700 truncate max-w-[300px]">{classroom.visio_link}</span>
                    </div>
                  ) : (
                    <span className="text-sm text-gray-400">{language === 'fr' ? 'Aucun lien configure' : 'No link configured'}</span>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => setEditingLink(true)} data-testid="edit-meet-link">
                    <LinkIcon className="w-3.5 h-3.5 mr-1" />
                    {hasLink ? (language === 'fr' ? 'Modifier' : 'Edit') : (language === 'fr' ? 'Configurer' : 'Configure')}
                  </Button>
                  {hasLink && (
                    <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-700" onClick={removeLink} data-testid="remove-meet-link">
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Join Button Card */}
      <Card className={`border-2 ${hasLink ? 'border-green-200 bg-green-50/30' : 'border-dashed border-gray-200'}`}>
        <CardContent className="p-8 text-center">
          <div className={`w-20 h-20 rounded-2xl mx-auto mb-5 flex items-center justify-center ${hasLink ? 'bg-green-100' : 'bg-gray-100'}`}>
            <Video className={`w-10 h-10 ${hasLink ? 'text-green-600' : 'text-gray-400'}`} />
          </div>
          {hasLink ? (
            <>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">{l.classroom}</h3>
              <p className="text-sm text-gray-500 mb-5">{classroom.promotion_label}</p>
              <a href={classroom.visio_link} target="_blank" rel="noopener noreferrer">
                <Button size="lg" className="gap-2 bg-green-600 hover:bg-green-700 text-white px-8" data-testid="join-classroom-button">
                  <Video className="w-5 h-5" />
                  {l.joinRoom}
                </Button>
              </a>
            </>
          ) : (
            <>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">{l.roomNotConfigured}</h3>
              <p className="text-sm text-gray-500 max-w-md mx-auto">{l.roomNotConfiguredDesc}</p>
              <Button size="lg" disabled className="gap-2 mt-5 opacity-50" data-testid="join-classroom-button-disabled">
                <Video className="w-5 h-5" />
                {l.joinRoom}
              </Button>
            </>
          )}
        </CardContent>
      </Card>

      {/* Course List */}
      <div>
        <h3 className="font-semibold text-gray-900 mb-3">{l.allCourses}</h3>
        <div className="space-y-2">
          {(classroom.courses || []).map(course => (
            <div key={course.id} className="flex items-center gap-3 p-3 bg-white rounded-lg border hover:border-blue-200 transition-colors" data-testid={`classroom-course-${course.code}`}>
              <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                <BookOpen className="w-5 h-5 text-blue-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-gray-900 text-sm">{course.name}</p>
                <p className="text-xs text-gray-500">{course.code} - {course.teacher_name}</p>
              </div>
              <Badge variant="outline" className="text-xs">{course.credits} cr.</Badge>
            </div>
          ))}
          {(classroom.courses || []).length === 0 && (
            <p className="text-sm text-gray-400 text-center py-4">{l.noClassrooms}</p>
          )}
        </div>
      </div>
    </div>
  );
};

// ============ SCHEDULE TAB ============
const ScheduleTab = ({ classroom, l }) => {
  const events = classroom.schedule_today || [];

  return (
    <div className="space-y-4">
      <h3 className="font-semibold text-gray-900">{l.todaySchedule}</h3>
      {events.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center">
            <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">{l.noScheduleToday}</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {events.map((event, idx) => (
            <Card key={event.id || idx} className="overflow-hidden" data-testid={`schedule-event-${idx}`}>
              <div className="flex">
                <div className="w-1.5" style={{ backgroundColor: event.color || '#3B82F6' }} />
                <CardContent className="p-4 flex-1">
                  <div className="flex items-start justify-between">
                    <div>
                      <h4 className="font-medium text-gray-900">{event.title}</h4>
                      {event.description && (
                        <p className="text-xs text-gray-500 mt-0.5">{event.description}</p>
                      )}
                    </div>
                    <Badge variant="outline" className="text-xs flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {event.start_time || '—'} - {event.end_time || '—'}
                    </Badge>
                  </div>
                  {event.room && (
                    <p className="text-xs text-gray-400 mt-2 flex items-center gap-1">
                      <Layers className="w-3 h-3" /> {event.room}
                    </p>
                  )}
                </CardContent>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

// ============ RESOURCES TAB ============
const ResourcesTab = ({ resources, loading, l, language, isTeacher, userId, onDelete }) => {
  if (loading) {
    return (
      <div className="space-y-3">
        {[1, 2].map(i => (
          <Card key={i} className="animate-pulse">
            <CardContent className="p-4"><div className="h-4 w-40 bg-gray-200 rounded mb-2" /><div className="h-3 w-24 bg-gray-200 rounded" /></CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (resources.length === 0) {
    return (
      <Card>
        <CardContent className="p-10 text-center">
          <BookMarked className="w-14 h-14 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">{l.noResources}</h3>
          <p className="text-gray-500 text-sm">{l.noResourcesDesc}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {resources.map(courseGroup => (
        <div key={courseGroup.course_id}>
          <div className="flex items-center gap-2 mb-3">
            <BookOpen className="w-4 h-4 text-[var(--tenant-primary)]" />
            <h4 className="font-semibold text-gray-900">{courseGroup.course_name}</h4>
            <Badge variant="secondary" className="text-xs font-mono">{courseGroup.course_code}</Badge>
            <span className="text-xs text-gray-400 ml-auto">{courseGroup.teacher_name}</span>
          </div>
          <div className="space-y-2 ml-1">
            {courseGroup.resources.map(resource => {
              const typeInfo = RESOURCE_TYPES[resource.resource_type] || RESOURCE_TYPES.other;
              const Icon = typeInfo.icon;
              return (
                <div key={resource.id} className="flex items-center gap-3 p-3 bg-white rounded-lg border hover:border-blue-200 transition-all group" data-testid={`resource-${resource.id}`}>
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 border ${typeInfo.color}`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 text-sm truncate">{resource.title}</p>
                    {resource.description && (
                      <p className="text-xs text-gray-500 truncate mt-0.5">{resource.description}</p>
                    )}
                    <p className="text-xs text-gray-400 mt-0.5">
                      {l.addedBy} {resource.added_by_name} - {new Date(resource.created_at).toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US')}
                    </p>
                  </div>
                  <div className="flex items-center gap-1">
                    <a href={resource.url} target="_blank" rel="noopener noreferrer">
                      <Button variant="outline" size="sm" className="gap-1.5 text-xs" data-testid={`open-resource-${resource.id}`}>
                        <ExternalLink className="w-3.5 h-3.5" />
                        {l.openLink}
                      </Button>
                    </a>
                    {isTeacher && resource.added_by === userId && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="w-8 h-8 text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={() => onDelete(resource.id)}
                        data-testid={`delete-resource-${resource.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};

// ============ ADD RESOURCE DIALOG ============
const AddResourceDialog = ({ courses, l, language, onClose, onSuccess }) => {
  const [form, setForm] = useState({
    course_id: '',
    title: '',
    url: '',
    resource_type: 'link',
    description: ''
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.course_id || !form.title || !form.url) {
      toast.error(language === 'fr' ? 'Remplissez les champs obligatoires' : 'Fill required fields');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/courses/${form.course_id}/resources`, {
        title: form.title,
        url: form.url,
        resource_type: form.resource_type,
        description: form.description || null
      });
      toast.success(language === 'fr' ? 'Ressource ajoutee' : 'Resource added');
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{l.addResource}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-2">
          <div>
            <Label>{l.selectCourse} *</Label>
            <Select value={form.course_id} onValueChange={(v) => setForm(prev => ({ ...prev, course_id: v }))}>
              <SelectTrigger data-testid="resource-course-select">
                <SelectValue placeholder={l.selectCourse} />
              </SelectTrigger>
              <SelectContent>
                {courses.map(c => (
                  <SelectItem key={c.id} value={c.id}>{c.code} - {c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>{l.resourceTitle} *</Label>
            <Input
              value={form.title}
              onChange={(e) => setForm(prev => ({ ...prev, title: e.target.value }))}
              placeholder={language === 'fr' ? 'Ex: Syllabus Algorithmique L1' : 'Ex: Algorithm Syllabus L1'}
              data-testid="resource-title-input"
            />
          </div>
          <div>
            <Label>{l.resourceUrl} *</Label>
            <Input
              value={form.url}
              onChange={(e) => setForm(prev => ({ ...prev, url: e.target.value }))}
              placeholder="https://..."
              type="url"
              data-testid="resource-url-input"
            />
          </div>
          <div>
            <Label>{l.resourceType}</Label>
            <Select value={form.resource_type} onValueChange={(v) => setForm(prev => ({ ...prev, resource_type: v }))}>
              <SelectTrigger data-testid="resource-type-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(RESOURCE_TYPES).map(([key, val]) => (
                  <SelectItem key={key} value={key}>{val.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>{l.resourceDesc}</Label>
            <Textarea
              value={form.description}
              onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
              placeholder={language === 'fr' ? 'Description de la ressource...' : 'Resource description...'}
              rows={2}
              data-testid="resource-desc-input"
            />
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="outline" onClick={onClose}>{l.cancel}</Button>
            <Button type="submit" disabled={saving} data-testid="save-resource-button">
              {saving ? (language === 'fr' ? 'Enregistrement...' : 'Saving...') : l.save}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default OnlineCoursesPage;
